import { useSettingsContext } from "@/components/common/settings-context/settings-context";
import { IElementWaypoint } from "@/hooks/use-placeholders";
import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import { selectIsPanoExtractedFromData } from "@/store/project-selector";
import { curryAppSelector } from "@/store/reselect";
import { useAppSelector } from "@/store/store-hooks";
import { WaypointsColoringOptions } from "@/store/view-options/view-options-slice";
import {
  DEFAULT_WAYPOINT_COLOR,
  INVALID_VALUE_COLOR,
  WAYPOINTS_ALTITUDE_GRADIENT,
  WAYPOINTS_DATE_GRADIENT,
} from "@/utils/waypoints-color-gradient";
import { holoBuilderColorToSphereXGColor } from "@/utils/waypoints-colors-mapping";
import { interpolateColorFromList } from "@faro-lotv/flat-ui";
import {
  HoloBuilderColors,
  IElementBase,
  IElementRoomSection,
  isIElementImg2d,
  isIElementImg360,
  isIElementRoomSection,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  selectAncestor,
  selectIElementWorldPosition,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { DateTime } from "luxon";
import { Color } from "three";
import {
  useWaypointAltitudeRange,
  useWaypointDateRange,
} from "../mode-data-context";
import { WaypointRange } from "../mode-selectors";
import {
  useColorOverrides,
  useMoveInteractionColorOverrides,
} from "./use-color-overrides";

/**
 * @returns A list of colors to apply to the waypoints in sheet mode
 * @param placeholders The list of waypoints being displayed
 * @param enableMoving Whether the move interaction is enabled
 * @param isInMinimap Whether the active waypoint should be highlighted in blue
 * @param activeWaypoint The currently active waypoint, if available
 * @param isPlaceholderDisabledForMove Function to compute on demand, whether a placeholder is allowed to be moved.
 * @param isPlaceholderDisabledForClick Function to compute on demand, whether a placeholder is allowed to be clicked.
 */
export function useSheetWaypointsColors(
  placeholders: IElementWaypoint[],
  enableMoving: boolean | undefined,
  isInMinimap?: boolean,
  activeWaypoint?: IElementWaypoint,
  isPlaceholderDisabledForMove?: (el: IElementWaypoint) => false | string,
  isPlaceholderDisabledForClick?: (el: IElementWaypoint) => boolean,
): Color[] {
  const colorsBase = useWaypointsColors(placeholders);
  const colorOverrides = useMoveInteractionColorOverrides(
    placeholders,
    enableMoving,
    isInMinimap,
    activeWaypoint,
    isPlaceholderDisabledForMove,
    isPlaceholderDisabledForClick,
  );

  return useColorOverrides(placeholders, colorsBase, colorOverrides);
}

/**
 * @returns The waypoints color based on altitude, if the option is enabled
 * @param panoImgs The list of panos and 2D photos
 */
function useWaypointsColors(panoImgs: IElementWaypoint[]): Color[] {
  const { waypointsColoring, waypointsCustomColor } = useSettingsContext();

  const altitudeRange = useWaypointAltitudeRange();
  const captureDateRange = useWaypointDateRange();

  return useAppSelector(
    selectWaypointsColors(
      panoImgs,
      waypointsColoring,
      altitudeRange,
      captureDateRange,
      waypointsCustomColor,
    ),
  );
}

export const selectWaypointsColors = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (_state, panoImgs: IElementWaypoint[]) => panoImgs,
      (
        _state,
        _panoImgs,
        waypointsColoring: WaypointsColoringOptions | undefined,
      ) => waypointsColoring,
      (
        _state,
        _panoImgs,
        _waypointsColoring,
        altitudeRange: WaypointRange | undefined,
      ) => altitudeRange,
      (
        _state,
        _panoImgs,
        _waypointsColoring,
        _altitudeRange,
        captureDateRange: WaypointRange | undefined,
      ) => captureDateRange,
      (
        _state,
        _panoImgs,
        _waypointsColoring,
        _altitudeRange,
        _captureDateRange,
        customColor: string | undefined,
      ) => customColor,
    ],
    (
      iElements,
      panoImgs,
      waypointsColoring,
      altitudeRange,
      captureDateRange,
      customColor,
    ) => {
      let result: Color[] | undefined;
      switch (waypointsColoring) {
        case WaypointsColoringOptions.byElevation: {
          if (!altitudeRange) break;
          // Coloring the waypoints by altitude
          const positions = panoImgs.map((p) =>
            isIElementImg360(p) &&
            selectIsPanoExtractedFromData(p)({ iElements })
              ? selectIElementWorldPosition(p.id)({ iElements })
              : undefined,
          );

          result = positions.map((p, index) => {
            if (!p) {
              return isIElementImg2d(panoImgs[index])
                ? new Color(DEFAULT_WAYPOINT_COLOR)
                : new Color(INVALID_VALUE_COLOR);
            }

            const alpha =
              (p[1] - altitudeRange.lowest) /
              (altitudeRange.highest - altitudeRange.lowest);
            return new Color(
              interpolateColorFromList(WAYPOINTS_ALTITUDE_GRADIENT, alpha),
            );
          });
          break;
        }
        case WaypointsColoringOptions.byCaptureDate: {
          if (!captureDateRange) break;
          // Coloring the waypoints by date
          result = waypointsColorsByDate(panoImgs);
          break;
        }
        case WaypointsColoringOptions.custom: {
          // A waypoint with its own color set (via its context menu) takes priority over the workspace-wide custom color
          result = panoImgs.map((panoImg) => {
            const ownColor = selectWaypointOwnColor(panoImg, iElements);
            if (ownColor) {
              return new Color(holoBuilderColorToSphereXGColor(ownColor));
            }
            return customColor
              ? new Color(customColor)
              : new Color(DEFAULT_WAYPOINT_COLOR);
          });
          break;
        }
      }
      return result ?? panoImgs.map(() => new Color(DEFAULT_WAYPOINT_COLOR));
    },
  ),
);

/**
 *
 * @param panoImgs The list of panos and 2D photos
 * @returns The list of colors for waypoints, representing the panos' capture date.
 */
function waypointsColorsByDate(
  panoImgs: IElementWaypoint[],
): Color[] | undefined {
  const dates = panoImgs.map((p) => DateTime.fromISO(p.createdAt));
  if (dates.length === 0) return;

  const minDate = DateTime.min(...dates)?.toMillis();
  const maxDate = DateTime.max(...dates)?.toMillis();

  if (!minDate || !maxDate || minDate >= maxDate) return;
  const invInterval = 1 / (maxDate - minDate);
  return dates.map((d) => {
    const alpha = (d.toMillis() - minDate) * invInterval;
    const colorString = interpolateColorFromList(
      WAYPOINTS_DATE_GRADIENT,
      alpha,
    );
    return new Color(colorString ?? INVALID_VALUE_COLOR);
  });
}

/** An iElement with a custom color stored in the metadata */
type IElementWithColor = IElementRoomSection & {
  metaDataMap: NonNullable<Required<IElementRoomSection["metaDataMap"]>>;
};

/**
 * @returns True if the input iElement has a valid waypoint color property
 * @param element The iElement to check
 */
function isIElementWithColor(
  element: IElementBase,
): element is IElementWithColor {
  return isIElementRoomSection(element) && !!element.metaDataMap?.waypointColor;
}

/**
 * @returns The color a waypoint was explicitly assigned via its context menu, if any
 * @param panoImg The waypoint to look up
 * @param iElements The iElements slice of the store
 */
function selectWaypointOwnColor(
  panoImg: IElementWaypoint,
  iElements: IElementsState,
): HoloBuilderColors | undefined {
  return selectAncestor(panoImg, isIElementWithColor)({ iElements })
    ?.metaDataMap.waypointColor;
}
